import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { ArrowLeft, Monitor, Smartphone, Save, Loader, Globe, MapPin, Languages, Check } from 'lucide-react'
import { useApi } from '../../hooks/useApi'
import { ContentHeader } from '../../components/layout/ContentHeader'

interface Client {
  id: number
  name: string
  domain: string
  country: string
  locationCity: string | null
  language: string
  defaultDevice: string
  autoCheck: boolean
}

const COUNTRIES = [
  { code: 'FR', name: 'France', flag: '🇫🇷' },
  { code: 'BE', name: 'Belgique', flag: '🇧🇪' },
  { code: 'CH', name: 'Suisse', flag: '🇨🇭' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦' },
  { code: 'LU', name: 'Luxembourg', flag: '🇱🇺' },
  { code: 'US', name: 'États-Unis', flag: '🇺🇸' },
  { code: 'GB', name: 'Royaume-Uni', flag: '🇬🇧' },
  { code: 'DE', name: 'Allemagne', flag: '🇩🇪' },
  { code: 'ES', name: 'Espagne', flag: '🇪🇸' },
  { code: 'IT', name: 'Italie', flag: '🇮🇹' },
  { code: 'NL', name: 'Pays-Bas', flag: '🇳🇱' },
  { code: 'PT', name: 'Portugal', flag: '🇵🇹' },
  { code: 'AT', name: 'Autriche', flag: '🇦🇹' },
]

const LANGUAGES = [
  { code: 'fr', name: 'Français' },
  { code: 'en', name: 'English' },
  { code: 'de', name: 'Deutsch' },
  { code: 'es', name: 'Español' },
  { code: 'it', name: 'Italiano' },
  { code: 'nl', name: 'Nederlands' },
  { code: 'pt', name: 'Português' },
]

export default function SearchEngines() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const { data: client, loading, error } = useApi<Client>(`/api/clients/${projectId}`)

  const [country, setCountry] = useState('FR')
  const [locationCity, setLocationCity] = useState('')
  const [language, setLanguage] = useState('fr')
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop')
  const [autoCheck, setAutoCheck] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [locQuery, setLocQuery] = useState('')
  const [locResults, setLocResults] = useState<Array<{ name: string; code: number; type: string }>>([])
  const [showLocDropdown, setShowLocDropdown] = useState(false)
  const locTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const locRef = useRef<HTMLDivElement>(null)

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (locRef.current && !locRef.current.contains(e.target as Node)) setShowLocDropdown(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  function searchLocations(query: string) {
    setLocQuery(query)
    if (locTimeout.current) clearTimeout(locTimeout.current)
    if (query.length < 2) { setLocResults([]); setShowLocDropdown(false); return }
    locTimeout.current = setTimeout(async () => {
      try {
        const res = await fetch(`/api/clients/locations/search?q=${encodeURIComponent(query)}&country=${country}`, { credentials: 'include' })
        const json = await res.json()
        setLocResults(json.data || [])
        setShowLocDropdown(true)
      } catch { setLocResults([]) }
    }, 300)
  }

  function selectLocation(loc: { name: string }) {
    setLocationCity(loc.name)
    setLocQuery(loc.name.split(',')[0])
    setShowLocDropdown(false)
  }

  useEffect(() => {
    if (client) {
      setCountry(client.country || 'FR')
      setLocationCity(client.locationCity || '')
      setLanguage(client.language || 'fr')
      setDevice((client.defaultDevice as 'desktop' | 'mobile') || 'desktop')
      setAutoCheck(client.autoCheck ?? false)
      setLocQuery(client.locationCity?.split(',')[0] || '')
    }
  }, [client])

  if (error === 'Unauthorized' || error === '401') {
    navigate('/login')
    return null
  }

  async function handleSave() {
    setSaving(true)
    setSaved(false)
    try {
      const res = await fetch(`/api/clients/${projectId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          country,
          locationCity: locationCity.trim() || null,
          language,
          defaultDevice: device,
          autoCheck,
        }),
      })
      if (res.ok) {
        setSaved(true)
        setTimeout(() => setSaved(false), 3000)
      }
    } catch {
      alert('Erreur réseau')
    } finally {
      setSaving(false)
    }
  }

  const selectedCountry = COUNTRIES.find(c => c.code === country)

  return (
    <div className="space-y-6">
      <ContentHeader
        breadcrumb={[
          { label: 'Dashboard', to: `/p/${projectId}` },
          { label: 'Classements', to: `/p/${projectId}/rankings` },
          { label: 'Paramètres' },
        ]}
        title="Moteurs de recherche"
        subtitle={client?.domain || ''}
      />

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader className="w-6 h-6 animate-spin text-[#8B8FA7]" strokeWidth={1.25} />
        </div>
      ) : (
        <>
          {/* Search engine config card */}
          <div className="bg-white border border-[#E2E4ED] rounded-xl p-6 space-y-6">
            <div>
              <h3 className="text-sm font-semibold text-[#1B2559] mb-1">Paramétrer les moteurs de recherche</h3>
              <p className="text-xs text-[#8B8FA7]">
                Suivez les positions de vos sites et de vos marques sur les requêtes qui vous intéressent, dans toutes les régions et sur tous les appareils.
              </p>
            </div>

            {/* Search engine tabs */}
            <div className="flex items-center gap-1 border-b border-[#E2E4ED] pb-3">
              <div className="flex items-center gap-2 px-4 py-2 bg-orion-primary/10 text-orion-primary rounded-lg text-sm font-medium">
                <Globe className="w-4 h-4" strokeWidth={1.25} />
                Google
              </div>
              <div className="flex items-center gap-2 px-4 py-2 text-[#8B8FA7] text-sm cursor-not-allowed opacity-50">
                AI Overviews
              </div>
              <div className="flex items-center gap-2 px-4 py-2 text-[#8B8FA7] text-sm cursor-not-allowed opacity-50">
                ChatGPT
              </div>

              <div className="ml-auto flex items-center gap-1 bg-[#F4F5FA] rounded-lg p-0.5">
                <button
                  onClick={() => setDevice('desktop')}
                  className={`p-2 rounded-md transition-colors ${device === 'desktop' ? 'bg-white shadow-sm text-[#1B2559]' : 'text-[#8B8FA7]'}`}
                  title="Desktop"
                >
                  <Monitor className="w-4 h-4" strokeWidth={1.25} />
                </button>
                <button
                  onClick={() => setDevice('mobile')}
                  className={`p-2 rounded-md transition-colors ${device === 'mobile' ? 'bg-white shadow-sm text-[#1B2559]' : 'text-[#8B8FA7]'}`}
                  title="Mobile"
                >
                  <Smartphone className="w-4 h-4" strokeWidth={1.25} />
                </button>
              </div>
            </div>

            {/* Country + Location */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-[#555A73] mb-1.5">
                  Pays
                </label>
                <select
                  value={country}
                  onChange={e => setCountry(e.target.value)}
                  className="w-full px-3 py-2.5 bg-[#F4F5FA] border border-[#E2E4ED] rounded-xl text-sm text-[#1B2559] focus:outline-none focus:border-orion-primary transition-colors"
                >
                  {COUNTRIES.map(c => (
                    <option key={c.code} value={c.code}>{c.flag} {c.name}</option>
                  ))}
                </select>
              </div>
              <div ref={locRef}>
                <label className="block text-xs font-medium text-[#555A73] mb-1.5">
                  Emplacement <span className="text-[#8B8FA7] font-normal">Optionnel</span>
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8B8FA7]" strokeWidth={1.25} />
                  <input
                    type="text"
                    value={showLocDropdown ? locQuery : (locationCity ? locationCity.split(',')[0] : locQuery)}
                    onChange={e => searchLocations(e.target.value)}
                    onFocus={() => { if (locQuery.length >= 2) setShowLocDropdown(true) }}
                    placeholder="Rechercher une ville..."
                    className="w-full pl-9 pr-3 py-2.5 bg-[#F4F5FA] border border-[#E2E4ED] rounded-xl text-sm text-[#1B2559] placeholder-[#8B8FA7] focus:outline-none focus:border-orion-primary transition-colors"
                  />
                  {showLocDropdown && locResults.length > 0 && (
                    <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-[#E2E4ED] rounded-xl shadow-lg max-h-60 overflow-auto">
                      {locResults.map(loc => (
                        <button
                          key={loc.code}
                          onClick={() => selectLocation(loc)}
                          className="w-full text-left px-4 py-2.5 hover:bg-[#F4F5FA] transition-colors flex items-center justify-between"
                        >
                          <span className="text-sm text-[#1B2559]">{loc.name}</span>
                          <span className="text-[10px] text-[#8B8FA7] capitalize">{loc.type}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {locationCity && (
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="text-xs text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded">{locationCity}</span>
                    <button onClick={() => { setLocationCity(''); setLocQuery('') }} className="text-[10px] text-red-400 hover:text-red-500">Supprimer</button>
                  </div>
                )}
              </div>
            </div>

            {/* Language */}
            <div className="max-w-xs">
              <label className="block text-xs font-medium text-[#555A73] mb-1.5">
                Langue de l'interface Google
              </label>
              <div className="relative">
                <Languages className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8B8FA7]" strokeWidth={1.25} />
                <select
                  value={language}
                  onChange={e => setLanguage(e.target.value)}
                  className="w-full pl-9 pr-3 py-2.5 bg-[#F4F5FA] border border-[#E2E4ED] rounded-xl text-sm text-[#1B2559] focus:outline-none focus:border-orion-primary transition-colors appearance-none"
                >
                  {LANGUAGES.map(l => (
                    <option key={l.code} value={l.code}>{l.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Auto check toggle */}
            <div className="flex items-center justify-between pt-2 pb-1">
              <div>
                <label className="text-xs font-medium text-[#555A73]">Verification automatique quotidienne</label>
                <p className="text-[11px] text-[#8B8FA7] mt-0.5">Verifie toutes les positions chaque jour a minuit. Consomme des credits DataForSEO.</p>
              </div>
              <button
                type="button"
                onClick={() => setAutoCheck(!autoCheck)}
                className={`relative w-10 h-5 rounded-full transition-colors ${autoCheck ? 'bg-orion-primary' : 'bg-[#E2E4ED]'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${autoCheck ? 'translate-x-5' : ''}`} />
              </button>
            </div>

            {/* Save button */}
            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-5 py-2.5 bg-orion-primary hover:bg-orion-primary-hover text-white rounded-xl text-sm font-medium transition-colors disabled:opacity-50"
              >
                {saving ? (
                  <Loader className="w-4 h-4 animate-spin" strokeWidth={1.25} />
                ) : saved ? (
                  <Check className="w-4 h-4" strokeWidth={1.25} />
                ) : (
                  <Save className="w-4 h-4" strokeWidth={1.25} />
                )}
                {saved ? 'Enregistré' : 'Enregistrer'}
              </button>
              {saved && <span className="text-xs text-emerald-600">Paramètres sauvegardés</span>}
            </div>
          </div>

          {/* Current config summary */}
          <div className="bg-white border border-[#E2E4ED] rounded-xl p-6">
            <h3 className="text-sm font-semibold text-[#1B2559] mb-4">Moteurs de recherche ajoutés au projet</h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E2E4ED]">
                  <th className="text-left py-2 text-xs text-[#555A73] font-medium uppercase tracking-wider">Moteur de recherche</th>
                  <th className="text-left py-2 text-xs text-[#555A73] font-medium uppercase tracking-wider">Emplacement</th>
                  <th className="text-left py-2 text-xs text-[#555A73] font-medium uppercase tracking-wider">Langue</th>
                  <th className="text-left py-2 text-xs text-[#555A73] font-medium uppercase tracking-wider">Appareil</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-[#F4F5FA]">
                  <td className="py-3 flex items-center gap-2">
                    <Globe className="w-4 h-4 text-[#4285F4]" strokeWidth={1.25} />
                    <span className="font-medium text-[#1B2559]">Google {selectedCountry?.name}</span>
                  </td>
                  <td className="py-3 text-[#555A73]">
                    <span className="mr-1.5">{selectedCountry?.flag}</span>
                    {locationCity ? locationCity.split(',')[0] + ', ' : ''}{selectedCountry?.name}
                  </td>
                  <td className="py-3 text-[#555A73] uppercase">{language}</td>
                  <td className="py-3">
                    <span className="inline-flex items-center gap-1 text-[#555A73]">
                      {device === 'mobile' ? <Smartphone className="w-3.5 h-3.5" strokeWidth={1.25} /> : <Monitor className="w-3.5 h-3.5" strokeWidth={1.25} />}
                      <span className="capitalize">{device}</span>
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}
